export const AI_PAGE_TARGETS = [
  'title',
  'price',
  'images',
  'description',
  'variants',
  'pricePicker',
  'bulkDiscounts',
  'eventDetails',
  'ticketTypes',
  'fields',
  'field',
  'discountCode',
  'paymentMethods',
  'fees',
  'tax',
  'total',
  'payButton',
  'confirmation',
  'upsell',
] as const;

export type AiPageTarget = (typeof AI_PAGE_TARGETS)[number];

export function isAiPageTarget(value: unknown): value is AiPageTarget {
  return typeof value === 'string' && (AI_PAGE_TARGETS as readonly string[]).includes(value);
}

export type AiPageType = 'checkout' | 'event' | 'form';

// The parts the store marks with data-ai-target for each page type. Events have no product, and a
// form has no prices, payment or tax; only the checkout editor sets up a one-click upsell.
export const AI_PAGE_TARGETS_BY_TYPE: Record<AiPageType, readonly AiPageTarget[]> = {
  checkout: [
    'title',
    'price',
    'images',
    'description',
    'variants',
    'pricePicker',
    'bulkDiscounts',
    'fields',
    'field',
    'discountCode',
    'paymentMethods',
    'fees',
    'tax',
    'total',
    'payButton',
    'confirmation',
    'upsell',
  ],
  event: [
    'title',
    'images',
    'description',
    'bulkDiscounts',
    'eventDetails',
    'ticketTypes',
    'fields',
    'field',
    'discountCode',
    'paymentMethods',
    'fees',
    'tax',
    'total',
    'payButton',
    'confirmation',
  ],
  form: ['title', 'images', 'description', 'fields', 'field', 'payButton', 'confirmation'],
};

// What the seller can upload to a page from the AI editor; the AI itself never uploads.
export const AI_UPLOAD_KINDS = ['images', 'files'] as const;

export type AiUploadKind = (typeof AI_UPLOAD_KINDS)[number];

// Images are the product's on a checkout page and the page gallery elsewhere; an event has no file downloads.
export const AI_UPLOAD_KINDS_BY_TYPE: Record<AiPageType, readonly AiUploadKind[]> = {
  checkout: ['images', 'files'],
  event: ['images'],
  form: ['images', 'files'],
};

export const AI_CHAT_MAX_ATTACHMENTS = 4;
export const AI_CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
// The model reads only these images; an SVG or HEIC is not one of them.
export const AI_CHAT_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
export const AI_CHAT_DOCUMENT_TYPES = [
  'application/pdf',
  'text/csv',
  'text/plain',
  'text/markdown',
] as const;

export type AiChatAttachmentType =
  | (typeof AI_CHAT_IMAGE_TYPES)[number]
  | (typeof AI_CHAT_DOCUMENT_TYPES)[number];
export type AiChatAttachmentKind = 'image' | 'pdf' | 'text';

// Browsers give a CSV an empty type or application/vnd.ms-excel, and Markdown often none.
const DOCUMENT_TYPE_BY_EXTENSION: Record<string, (typeof AI_CHAT_DOCUMENT_TYPES)[number]> = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  txt: 'text/plain',
  md: 'text/markdown',
  markdown: 'text/markdown',
};

export const AI_CHAT_ATTACHMENT_ACCEPT = [
  ...AI_CHAT_IMAGE_TYPES,
  ...AI_CHAT_DOCUMENT_TYPES,
  ...Object.keys(DOCUMENT_TYPE_BY_EXTENSION).map(extension => `.${extension}`),
].join(',');

const ATTACHMENT_TYPES: readonly string[] = [...AI_CHAT_IMAGE_TYPES, ...AI_CHAT_DOCUMENT_TYPES];

type NamedFile = { name?: string | null; type?: string | null };

// An image is only ever known by its type: the model cannot read one whose type is wrong.
export function aiChatAttachmentType({ name, type }: NamedFile): AiChatAttachmentType | undefined {
  const mimeType = (type ?? '').split(';')[0].trim().toLowerCase();
  if (ATTACHMENT_TYPES.includes(mimeType)) return mimeType as AiChatAttachmentType;
  if (mimeType.startsWith('image/')) return undefined;
  const extension = /\.([a-z0-9]+)$/i.exec(name ?? '')?.[1].toLowerCase();
  return extension ? DOCUMENT_TYPE_BY_EXTENSION[extension] : undefined;
}

export function aiChatAttachmentKind(file: NamedFile): AiChatAttachmentKind | undefined {
  const type = aiChatAttachmentType(file);
  if (!type) return undefined;
  if (type === 'application/pdf') return 'pdf';
  return type.startsWith('image/') ? 'image' : 'text';
}

// What a page made by Create with AI is called until its first message builds it.
export const AI_BLANK_TITLE_BY_TYPE: Record<AiPageType, string> = {
  checkout: 'Untitled product',
  event: 'Untitled event',
  form: 'Untitled form',
};

// The title buyers see: the product title on a checkout page, the page title on an event or form.
// An empty title is not blank: forms are created with one and are often live with fields and content.
export function isBlankAiPage(pageType: AiPageType, title: string | null | undefined): boolean {
  return (title ?? '').trim() === AI_BLANK_TITLE_BY_TYPE[pageType];
}

// The dashboard tells these API errors apart by their exact wording.
export const AI_LIMIT_MESSAGE =
  'The assistant stopped before finishing. Check the page and try again.';
export const AI_UNAVAILABLE_MESSAGE =
  'The AI assistant is unavailable right now. Try again in a minute.';
export const AI_CONVERSATION_MISMATCH_MESSAGE =
  'This conversation belongs to a different page. Start a new one.';
export const AI_OFF_TOPIC_REPLY =
  'I can only help with your Checkout Page account, like building and editing your checkout pages, events and forms.';

// After this long without a message the API no longer continues a conversation's responses, so the dashboard
// starts a new one.
export const AI_CONVERSATION_TTL_SECONDS = 24 * 60 * 60;

export const AI_CAPTURE_MAX_IMAGE_LENGTH = 2_000_000;

// One key per page-editor section the in-app AI's tools can change. Saved chats store the key, so it stays
// when a section is renamed: checkout-fields is the Form fields section.
export const AI_GUIDE_KEYS: Record<AiPageType, readonly string[]> = {
  checkout: [
    'details',
    'description',
    'image-gallery',
    'variants',
    'file-downloads',
    'inventory',
    'license-keys',
    'bulk-discounts',
    'order-bumps',
    'checkout-fields',
    'discount-code',
    'manual-payments',
    'payment-methods',
    'additional-fees',
    'tax',
    'purchase-limits',
    'cart-abandonment',
    'dynamic-price',
    'payment-confirmation',
    'email-confirmation',
    'one-click-upsell',
    'invoices',
    'email-notifications',
    'tracking-pixels',
    'url-slug',
    'google-index',
    'redirect-page',
    'pop-up-behavior',
    'theme',
  ],
  event: [
    'details',
    'description',
    'image-gallery',
    'ticket-types',
    'bulk-discounts',
    'add-ons',
    'checkout-fields',
    'discount-code',
    'manual-payments',
    'payment-methods',
    'additional-fees',
    'tax',
    'cart-abandonment',
    'dynamic-info',
    'payment-confirmation',
    'email-confirmation',
    'invoices',
    'email-notifications',
    'tracking-pixels',
    'url-slug',
    'google-index',
    'redirect-page',
    'pop-up-behavior',
    'theme',
  ],
  form: [
    'fields',
    'description',
    'image-gallery',
    'file-downloads',
    'submission-limits',
    'after-submission',
    'email-confirmation',
    'email-notifications',
    'tracking-pixels',
    'url-slug',
    'dynamic-info',
    'google-index',
    'redirect-page',
    'pop-up-behavior',
    'theme',
  ],
};

export function isAiGuideKey(pageType: string, key: unknown): boolean {
  if (typeof key !== 'string' || !Object.hasOwn(AI_GUIDE_KEYS, pageType)) return false;
  return AI_GUIDE_KEYS[pageType as AiPageType].includes(key);
}
